import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home } from "lucide-react";

import { useAuth } from "@/hooks/useAuth";

const logo = "/vouchee-header-logo.png";

function titleize(segment) {
  if (!segment) return "";
  return segment
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getDashboardHome(user) {
  const accountType = user?.account_type;
  if (accountType === "admin") return "/admin";
  if (accountType === "business_owner" || accountType === "business")
    return "/business-dashboard";
  return "/dashboard";
}

function getPageTitle(pathname) {
  const clean = (pathname || "/").split("?")[0];

  const known = new Map([
    ["/dashboard", "Dashboard"],
    ["/business-dashboard", "Business Dashboard"],
    ["/admin", "Admin Dashboard"],
    ["/admin/fraud-alerts", "Fraud Alerts"],
    ["/admin/settings/subscription-plans", "Subscription Plans"],
    ["/admin/settings/promo-codes", "Promo Codes"],
    ["/dashboard/invoicing", "Invoicing"],
    ["/dashboard/partnerships", "Partnerships"],
    ["/dashboard/analytics", "Analytics"],
    ["/dashboard/transactions", "Transactions"],
    ["/dashboard/services", "Service Catalog"],
    ["/dashboard/settings", "Settings"],
    ["/dashboard/commission-settings", "Commission Settings"],
    ["/dashboard/payouts", "Payouts"],
    ["/dashboard/rolodex", "Rolodex"],
    ["/dashboard/smart-lists", "Smart Lists"],
    ["/customers", "Customer Management"],
    ["/business-dashboard/import-customers", "Import Customers"],
    ["/business-contacts", "Customer Contacts"],
    ["/partnerships", "Partnerships"],
    ["/proposals", "Proposals"],
    ["/service-catalog", "Service Catalog"],
    ["/discount-codes", "Discount Codes"],
    ["/earnings", "Earnings"],
    ["/gift-cards", "Gift Cards"],
  ]);

  if (known.has(clean)) return known.get(clean);

  // Handle nested pages like /dashboard/smart-lists/:id, /admin/manual-review, etc.
  const segments = clean.split("/").filter(Boolean);
  if (segments.length === 0) return "Dashboard";

  const last = segments[segments.length - 1];
  const prev = segments[segments.length - 2];

  if (segments[0] === "dashboard" && segments.length === 1) return "Dashboard";
  if (segments[0] === "admin" && segments.length === 1)
    return "Admin Dashboard";

  // Prefer the parent segment if the last segment looks like an id.
  const looksLikeId = /^[0-9a-f-]{8,}$/i.test(last);
  const label = looksLikeId ? prev : last;
  return titleize(label);
}

export default function DashboardHeader() {
  const { user } = useAuth();
  const location = useLocation();

  const dashboardHome = useMemo(() => getDashboardHome(user), [user]);
  const title = useMemo(
    () => getPageTitle(location.pathname),
    [location.pathname],
  );

  return (
    <header className="bg-white border-b sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to={dashboardHome}
              className="flex items-center gap-2 shrink-0"
              aria-label="Go to dashboard"
            >
              <img
                src={logo}
                alt="Vouchee"
                className="h-9 sm:h-10 w-auto max-w-[120px] object-contain"
              />
            </Link>
            <div className="min-w-0">
              <div className="text-sm text-gray-500">You are in</div>
              <div className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                {title}
              </div>
            </div>
          </div>

          <Link
            to={dashboardHome}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-2 sm:px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 shrink-0"
            aria-label="Back to dashboard"
          >
            <Home className="w-4 h-4" />
            <span className="hidden sm:inline">Dashboard</span>
          </Link>
        </div>
      </div>
    </header>
  );
}
